<template>
  <div :id="componentId" class="inno-carousel overflow-hidden">
    <div class="container mx-auto mb-8 flex max-w-screen-xl items-center justify-between px-0">
      <VcTypography :tag="titleComponent" v-if="title">
        {{ title }}
      </VcTypography>
      <div v-else></div>
      <!-- Navigation buttons-->
      <div v-if="navigation && showNavigation" class="inno-carousel__navigation">
        <div class="inno-carousel__btn inno-carousel__btn--prev cursor-pointer">
          <VcIcon class="relative right-[1px] text-white" name="chevron-left" :size="14" />
        </div>

        <div class="inno-carousel__btn inno-carousel__btn--next ml-2 cursor-pointer">
          <VcIcon class="relative right-[-1px] text-white" name="chevron-right" :size="14" />
        </div>
      </div>
    </div>

    <div class="inno-carousel__wrapper">
      <Swiper
        :slides-per-view="1"
        :modules="modules"
        :looped-slides="slides.length"
        :navigation="navigationParams"
        :pagination="paginationParams"
        v-bind="attrs"
        :simulate-touch="showNavigation"
        class="w-full"
        v-on="listeners"
      >
        <SwiperSlide v-for="(slide, index) in slides" :key="index">
          <slot name="slide" v-bind="{ slide, index }">
            {{ slide }}
          </slot>
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from "lodash";
import { Pagination, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue"; // eslint-disable-line import/no-unresolved
import { computed, getCurrentInstance } from "vue";
import type { NavigationOptions, PaginationOptions } from "swiper/types";
import type { PropType } from "vue";
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";

const props = defineProps({
  slides: {
    type: Array as PropType<any[]>,
    default: () => [],
  },

  options: {
    type: Object as PropType<ICarouselOptions>,
    default: () => ({}),
  },

  navigation: {
    type: Boolean,
    default: false,
  },

  pagination: {
    type: Boolean,
    default: false,
  },

  title: {
    type: String,
    default: "",
  },

  titleComponent: {
    type: String,
    default: "",
  },
});

const componentId = `inno-carousel_${getCurrentInstance()!.uid}`;
const modules = [Pagination, Navigation];

const listeners = computed(() => props.options.on ?? {});
const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller("xl");

const attrs = computed<Omit<ICarouselOptions, "on">>(() => {
  const options = _.clone(props.options);
  delete options.on;
  return options;
});

const showNavigation = computed(() => (isMobile.value ? true : props.slides.length > 3));

const navigationParams = computed<NavigationOptions | boolean>(() =>
  props.navigation
    ? {
        prevEl: `#${componentId} .inno-carousel__btn--prev`,
        nextEl: `#${componentId} .inno-carousel__btn--next`,
        lockClass: "inno-carousel__btn--lock",
        hiddenClass: "inno-carousel__btn--hidden",
        disabledClass: "inno-carousel__btn--disabled",
      }
    : false,
);

const paginationParams = computed<PaginationOptions | boolean>(() =>
  props.pagination
    ? {
        clickable: true,
      }
    : false,
);
</script>

<style lang="scss">
.inno-carousel {
  $self: &;

  --navigation-size: 36px;
  --navigation-offset: 0px;
  --pagination-offset: -5px;

  --swiper-pagination-bullet-size: 13px;
  --swiper-pagination-bullet-inactive-color: transparent;
  --swiper-pagination-bullet-inactive-opacity: 1;

  &__wrapper {
    @apply mx-auto relative overflow-visible;
  }

  &--navigation {
    #{$self}__wrapper {
    }
  }

  &__navigation {
    @apply flex justify-center items-center h-6;
  }

  &__btn {
    @apply flex justify-center items-center rounded-full bg-[var(--color-primary-500)] w-6 h-6;
    color: #fff;

    &--prev {
      @apply left-0;
    }

    &--next {
      @apply right-0;
    }

    &--disabled {
      @apply cursor-auto pointer-events-none opacity-30;
    }

    &--lock {
      @apply hidden;
    }
  }

  .swiper-pagination-fraction,
  .swiper-pagination-custom,
  .swiper-horizontal > .swiper-pagination-bullets,
  .swiper-pagination-bullets.swiper-pagination-horizontal {
    bottom: var(--pagination-offset, 0);
  }

  .swiper-pagination-bullet {
    @apply border-2 border-[color:var(--color-primary-500)];
  }

  .swiper {
    overflow: visible;
  }
}
</style>
